import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAudit } from "../context/AuditContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import API from "../lib/api";
import {
  ArrowRight,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  CircleDot,
  Eye,
  FileText,
  FolderOpen,
  Loader2,
  RefreshCw,
  Search,
  Trash2,
  Upload,
} from "lucide-react";
import { PageLayout } from "../components/PageLayout";
import FilePreviewDialog from "../components/FilePreviewDialog";

const DOC_TYPES = [
  { value: "regulatory", label: "Regulatory Guidelines" },
  { value: "policy", label: "Internal Policy" },
  { value: "sop", label: "SOP / Process" },
  { value: "rcm", label: "Risk & Control Matrix" },
  { value: "historical", label: "Historical Audit Report" },
  { value: "checklist", label: "Audit Checklist" },
  { value: "fssai", label: "FSSAI Regulation / License" },
  { value: "gst", label: "GST Policy / Returns" },
  { value: "dpdp", label: "DPDP / Privacy Framework" },
  { value: "partner_compliance", label: "Partner Compliance Policy" },
  { value: "platform_sop", label: "Platform SOP" },
  { value: "incident_policy", label: "Incident Management Policy" },
  { value: "insurance_policy", label: "Insurance Policy Formulation" },
  { value: "underwriting_guidelines", label: "Life Underwriting Guidelines" },
  { value: "medical_report", label: "Medical Examiner Report (MER)" },
  { value: "financial_questionnaire", label: "Financial Questionnaire" },
  { value: "kyc_aml_form", label: "KYC / AML / PPATK Forms" },
  { value: "claims_sop", label: "Claims Processing SOP" },
  { value: "actuarial_report", label: "Actuarial Valuation Report" },
  { value: "ojk_rbc_report", label: "OJK RBC / Solvency Report" },
  { value: "reinsurance_treaty", label: "Reinsurance Treaty / Recoveries" },
  { value: "takaful_declaration", label: "Takaful / Sharia Compliance" },
  { value: "pii_data_policy", label: "POJK Data Privacy Policy" },
];

const STATUS_META = {
  uploaded: { label: "uploaded" },
  ingesting: { label: "processing" },
  parsing: { label: "processing" },
  extracting: { label: "processing" },
  ready: { label: "ready" },
  failed: { label: "failed" },
};

export default function Phase2Documents() {
  const navigate = useNavigate();
  const { auditRun, advancePhase, documents: contextDocs, dataVersion } = useAudit();

  const [documents, setDocuments] = useState([]);
  const [artifacts, setArtifacts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState("regulatory");
  const [searchTerm, setSearchTerm] = useState("");
  const [previewDoc, setPreviewDoc] = useState(null);

  const fetchDocs = useCallback(async () => {
    if (!auditRun?.id) return;
    try {
      const [{ data: docsData }, { data: artifactsData }] = await Promise.all([
        API.get(`/audit-runs/${auditRun.id}/documents`),
        API.get(`/audit-runs/${auditRun.id}/artifacts`).catch(() => ({ data: [] })),
      ]);

      const normalizedDocs = Array.isArray(docsData) ? docsData : [];
      const normalizedArtifacts =
        artifactsData && Array.isArray(artifactsData.artifacts)
          ? artifactsData.artifacts
          : Array.isArray(artifactsData)
            ? artifactsData
            : [];

      setDocuments(normalizedDocs.filter((d) => d.doc_type !== "artifact"));
      setArtifacts(normalizedArtifacts);
    } catch (error) {
      console.error("Failed to fetch Phase 2 documents:", error);
    }
  }, [auditRun?.id]);

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs, dataVersion]);

  useEffect(() => {
    if (contextDocs && contextDocs.length > 0) {
      setDocuments(contextDocs.filter((d) => d.doc_type !== "artifact"));
    }
  }, [contextDocs]);

  const handleContinue = useCallback(async () => {
    if (typeof advancePhase === "function") {
      await advancePhase(3);
    }
    navigate("/phase/3");
  }, [advancePhase, navigate]);

  useEffect(() => {
    const hasProcessing = documents.some((d) => !["ready", "uploaded"].includes(String(d.status || "").toLowerCase()));
    if (!hasProcessing) return;
    const interval = setInterval(fetchDocs, 3000);
    return () => clearInterval(interval);
  }, [documents, fetchDocs]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !auditRun?.id) return;

    setUploading(true);
    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("doc_type", docType);

      try {
        const { data } = await API.post(`/audit-runs/${auditRun.id}/documents`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (data && data.doc_type !== "artifact") {
          setDocuments((prev) => {
            const next = Array.isArray(prev) ? [...prev] : [];
            const existingIdx = next.findIndex((doc) => doc.id === data.id);
            if (existingIdx >= 0) {
              next[existingIdx] = data;
              return next;
            }
            return [data, ...next];
          });
        }
        toast.success(`Uploaded: ${file.name}`);
      } catch (error) {
        const detail = error?.response?.data?.detail;
        if (detail && typeof detail === "object" && detail.message) {
          toast.error(detail.message, { duration: 8000 });
        } else if (typeof detail === "string") {
          toast.error(detail, { duration: 6000 });
        } else {
          toast.error(`Upload failed: ${file.name}`);
        }
      }
    }

    setUploading(false);
    await fetchDocs();
    e.target.value = "";
  };

  const handleReclassify = async (docId, classification) => {
    try {
      await API.put(`/audit-runs/${auditRun.id}/documents/${docId}/classify`, { classification });
      await fetchDocs();
    } catch (error) {
      toast.error("Failed to reclassify");
    }
  };

  const handleDelete = async (docId, filename) => {
    try {
      await API.delete(`/audit-runs/${auditRun.id}/documents/${docId}`);
      toast.success(`Deleted: ${filename}`);
      await fetchDocs();
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  const isBadDocument = (doc) => {
    const badIndicators = ["blocked", "just a moment", "cloudflare", "access denied", "error", "403", "404"];
    const filename = String(doc?.filename || "").toLowerCase();
    const classification = String(doc?.classification || "").toLowerCase();
    return (
      badIndicators.some((indicator) => filename.includes(indicator) || classification.includes(indicator)) ||
      (doc?.file_size && doc.file_size < 1000)
    );
  };

  const allReady = documents.length > 0 && documents.every((d) => String(d.status || "").toLowerCase() === "ready");
  const processingCount = documents.filter((d) => !["ready", "uploaded"].includes(String(d.status || "").toLowerCase())).length;
  const readyCount = documents.filter((d) => String(d.status || "").toLowerCase() === "ready").length;
  const failedCount = documents.filter((d) => String(d.status || "").toLowerCase() === "failed" || isBadDocument(d)).length;

  const totalSizeMb = documents.reduce((sum, doc) => sum + ((doc.file_size || 0) / 1024 / 1024), 0);

  const docList = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return [...documents]
      .filter((doc) => {
      const haystack = [doc.filename, doc.doc_type, doc.classification, doc.status].filter(Boolean).join(" ").toLowerCase();
      return !q || haystack.includes(q);
    })
      .sort((a, b) => new Date(b.uploaded_at || b.created_at || b.updated_at || 0).getTime() - new Date(a.uploaded_at || a.created_at || a.updated_at || 0).getTime());
  }, [documents, searchTerm]);

  const formatDocTypeLabel = (value) => {
    const label = String(value || "").trim();
    if (!label) return "Document";
    return label.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const formatFileSize = (size) => {
    const bytes = Number(size || 0);
    if (!bytes) return "0 KB";
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${bytes} B`;
  };

  const formatTimestamp = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(date);
  };

  return (
    <PageLayout
      icon={FileText}
      title="Phase 2: Document Ingestion"
      subtitle="Upload regulatory guidelines, internal policies, SOPs, and Risk Control Matrices (RCM) for AI parsing."
      className="space-y-6 max-w-7xl mx-auto pb-12"
      data-testid="phase-2"
      actions={
        <div className="flex items-center gap-3">
          {auditRun?.name && (
            <Badge variant="outline" className="hidden sm:inline-flex bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30 text-xs font-semibold px-3 py-1">
              {auditRun.name}
            </Badge>
          )}
          <Button
            className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs h-9 px-5 shadow-md shadow-emerald-600/30 border-0 transition-all active:scale-95"
            onClick={handleContinue}
            disabled={documents.length === 0}
            data-testid="phase-2-continue-btn"
          >
            Continue to Controls <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
        </div>
      }
    >
      {/* COMPACT METRICS STRIP */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="rounded-2xl border border-border/80 bg-card p-4 hover:border-emerald-500/30 transition-all">
          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground block">Total Documents</span>
          <span className="text-2xl sm:text-3xl font-black text-foreground mt-1 block">{documents.length}</span>
        </Card>

        <Card className="rounded-2xl border border-border/80 bg-card p-4 hover:border-emerald-500/30 transition-all">
          <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400 block">Ready</span>
          <span className="text-2xl sm:text-3xl font-black text-emerald-600 dark:text-emerald-400 mt-1 block">{readyCount}</span>
        </Card>

        <Card className="rounded-2xl border border-border/80 bg-card p-4 hover:border-emerald-500/30 transition-all">
          <span className="text-[10px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400 block">Processing</span>
          <span className="text-2xl sm:text-3xl font-black text-amber-600 dark:text-amber-400 mt-1 block">{processingCount}</span>
        </Card>

        <Card className="rounded-2xl border border-border/80 bg-card p-4 hover:border-emerald-500/30 transition-all">
          <span className="text-[10px] font-bold uppercase tracking-wider text-teal-600 dark:text-teal-400 block">Ingested Artifacts</span>
          <span className="text-2xl sm:text-3xl font-black text-teal-600 dark:text-teal-400 mt-1 block">{documents.length}</span>
        </Card>
      </div>

      {/* UPLOAD PANEL */}
      <Card className="rounded-3xl border-border/80 shadow-sm overflow-hidden">
        <CardHeader className="bg-muted/40 py-4 px-6 sm:px-8 border-b border-border/60">
          <CardTitle className="text-sm font-bold flex items-center gap-2 text-foreground">
            <Upload className="w-4 h-4 text-emerald-500" />
            Upload Governance & Policy Documents
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 sm:p-8 space-y-6">
          <div className="grid gap-6 lg:grid-cols-12 items-end">
            <div className="lg:col-span-4 space-y-2">
              <Label htmlFor="phase2-doc-type" className="text-xs font-bold text-foreground">
                Document Classification Category
              </Label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger id="phase2-doc-type" className="h-11 rounded-xl bg-card border-border text-xs font-medium px-3.5" data-testid="select-doc-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  {DOC_TYPES.map((option) => (
                    <SelectItem key={option.value} value={option.value} className="text-xs">
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="lg:col-span-8">
              <label className="flex flex-col items-center justify-center p-8 sm:p-10 rounded-3xl border-2 border-dashed border-emerald-500/30 hover:border-emerald-500/60 bg-emerald-500/[0.02] hover:bg-emerald-500/[0.05] cursor-pointer transition-all text-center group">
                <input
                  type="file"
                  multiple
                  onChange={handleUpload}
                  className="sr-only"
                  accept=".pdf,.txt,.md,.docx,.doc"
                  data-testid="file-upload-input"
                  disabled={!auditRun || uploading}
                />
                <div className="h-12 w-12 rounded-2xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                  {uploading ? <Loader2 className="h-6 w-6 animate-spin" /> : <Upload className="h-6 w-6" />}
                </div>
                <span className="text-xs sm:text-sm font-bold text-foreground">
                  {uploading ? "Uploading & Digesting Documents..." : "Click to Upload or Drag & Drop Documents"}
                </span>
                <span className="text-[11px] text-muted-foreground mt-1">
                  Supported formats: PDF, DOCX, DOC, MD, TXT
                </span>
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* DOCUMENT LIST DATA TABLE */}
      <Card className="rounded-3xl border-border/80 shadow-sm overflow-hidden">
        <CardHeader className="bg-muted/40 border-b border-border/60 py-4 px-6 sm:px-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <CardTitle className="text-sm font-bold text-foreground flex items-center gap-2">
            <FolderOpen className="w-4 h-4 text-emerald-500" />
            Uploaded Documents List ({documents.length})
          </CardTitle>

          <div className="flex items-center gap-3">
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by filename or classification..."
                className="h-9 pl-8 pr-3 text-xs rounded-xl bg-card border-border/80 focus-visible:ring-emerald-500/30"
              />
            </div>
            <Button variant="outline" size="sm" onClick={fetchDocs} className="rounded-xl h-9 text-xs font-semibold px-3.5">
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="border-b border-border/60 bg-muted/20 text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
                  <th className="py-3.5 px-6">Filename</th>
                  <th className="py-3.5 px-5">Category</th>
                  <th className="py-3.5 px-5">Classification</th>
                  <th className="py-3.5 px-5">Status</th>
                  <th className="py-3.5 px-5">File Size</th>
                  <th className="py-3.5 px-5">Uploaded Date</th>
                  <th className="py-3.5 px-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {docList.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-16 text-center text-muted-foreground">
                      <FolderOpen className="mx-auto h-10 w-10 text-muted-foreground/40 mb-3" />
                      <p className="font-semibold text-foreground text-sm">No documents found</p>
                      <p className="text-xs mt-1">Upload PDF or DOCX documents to begin audit parsing.</p>
                    </td>
                  </tr>
                ) : (
                  docList.map((doc) => {
                    const status = String(doc.status || "uploaded").toLowerCase();
                    const meta = STATUS_META[status] || STATUS_META.uploaded;
                    const isFailed = status === "failed" || isBadDocument(doc);

                    return (
                      <tr key={doc.id} className="hover:bg-accent/40 transition-colors group">
                        <td className="py-4 px-6 font-semibold text-foreground">
                          <div className="flex items-center gap-2.5">
                            <FileText className="h-4 w-4 text-emerald-500 shrink-0" />
                            <span className="truncate max-w-xs">{doc.filename}</span>
                          </div>
                        </td>
                        <td className="py-4 px-5">
                          <Badge variant="outline" className="text-[10px] font-semibold bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30 px-2.5 py-0.5">
                            {formatDocTypeLabel(doc.doc_type)}
                          </Badge>
                        </td>
                        <td className="py-4 px-5">
                          <input
                            className="h-8 w-full max-w-[170px] rounded-xl border border-border/80 bg-card px-3 text-xs font-medium text-foreground focus:outline-none focus:border-emerald-500 transition-colors"
                            defaultValue={doc.classification || ""}
                            onBlur={(e) => e.target.value !== doc.classification && handleReclassify(doc.id, e.target.value)}
                            placeholder="Tag classification..."
                          />
                        </td>
                        <td className="py-4 px-5">
                          <Badge
                            className={`text-[10px] font-bold px-2.5 py-0.5 ${
                              meta.label === "ready"
                                ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30"
                                : meta.label === "failed"
                                  ? "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30"
                                  : "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30"
                            }`}
                          >
                            {meta.label === "ready" ? "Ready" : meta.label === "failed" ? "Failed" : "Processing"}
                          </Badge>
                        </td>
                        <td className="py-4 px-5 font-mono text-[11px] text-muted-foreground">
                          {formatFileSize(doc.file_size)}
                        </td>
                        <td className="py-4 px-5 font-mono text-[11px] text-muted-foreground">
                          {formatTimestamp(doc.uploaded_at || doc.created_at || doc.updated_at)}
                        </td>
                        <td className="py-4 px-6 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 rounded-xl text-muted-foreground hover:text-emerald-600 hover:bg-emerald-500/10 transition-colors"
                              onClick={() => setPreviewDoc(doc)}
                              title="Preview file"
                              data-testid={`preview-doc-${doc.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 rounded-xl text-muted-foreground hover:text-red-600 hover:bg-red-500/10 transition-colors"
                              onClick={() => handleDelete(doc.id, doc.filename)}
                              title="Delete document"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* BOTTOM ACTIONS BAR */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 sm:p-5 bg-card border border-border/80 rounded-2xl shadow-sm">
        <span className="text-xs text-muted-foreground font-medium">
          Ready to advance to control extraction once documents are ingested.
        </span>
        <div className="flex items-center gap-3">
          <Button variant="outline" className="rounded-xl text-xs font-semibold h-10 px-4" onClick={() => toast.success("Draft saved")}>
            Save Draft
          </Button>
          <Button
            className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs h-10 px-5 shadow-md shadow-emerald-600/30 active:scale-95 transition-all border-0"
            onClick={handleContinue}
            disabled={documents.length === 0}
          >
            Continue to Control Mapping <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
        </div>
      </div>

      <FilePreviewDialog
        open={!!previewDoc}
        onOpenChange={(open) => {
          if (!open) setPreviewDoc(null);
        }}
        auditRunId={auditRun?.id}
        item={previewDoc}
      />
    </PageLayout>
  );
}
